import { type Candidate } from "../types/candidate";
import { type JobCriterion } from "../types/job";

/** Split a unified `criteria` list into the must- vs. should-have name
 * arrays the legacy skill-match algorithm expects. Names of required
 * criteria become must-haves; the rest are should-haves. */
export function criteriaToSkillSplit(
  criteria: readonly JobCriterion[],
): { mustHaves: string[]; shouldHaves: string[] } {
  const mustHaves: string[] = [];
  const shouldHaves: string[] = [];
  for (const c of criteria) {
    if (c.required) {
      mustHaves.push(c.name);
    } else {
      shouldHaves.push(c.name);
    }
  }
  return { mustHaves, shouldHaves };
}

export interface SkillMatchSummary {
  mustHit: readonly string[];
  mustMiss: readonly string[];
  shouldHit: readonly string[];
  shouldMiss: readonly string[];
}

/** Tokenise a skill string for whole-token matching. Lower-cases, then
 * splits on every character that is NOT a letter, digit, `+` or `#`, so
 * `.`, `/`, `-`, whitespace and punctuation are separators while common
 * tech-skill glyphs survive:
 *
 *   "C++"          → ["c++"]             "C#"   → ["c#"]
 *   "Node.js"      → ["node", "js"]      ".NET" → ["net"]
 *   "React Native" → ["react", "native"]
 *
 * Dotted compounds split into their parts on purpose so "Node" matches
 * "Node.js"; `+` / `#` stay attached so "C" does NOT match "C++" / "C#". */
function tokenize(skill: string): string[] {
  return skill
    .toLowerCase()
    .split(/[^a-z0-9+#]+/)
    .filter((t) => t.length > 0);
}

/** Whole-token skill matcher shared by the scoring auto-match
 * (`computeSkillMatch`) and the Longlist Must-Have filter
 * (`candidate-filtering.ts::isCriterionMet`). A skill matches the needle
 * when the needle's token sequence appears as a CONTIGUOUS run of tokens
 * inside the skill — so matching is by token boundary, not substring:
 * "Java" does NOT match "JavaScript", while "React" matches "React Native"
 * and "Node" matches "Node.js". Case-insensitive via `tokenize`. */
export function skillMatches(skill: string, needle: string): boolean {
  const hay = tokenize(skill);
  const need = tokenize(needle);
  if (need.length === 0 || hay.length < need.length) return false;
  for (let i = 0; i + need.length <= hay.length; i++) {
    let matched = true;
    for (let j = 0; j < need.length; j++) {
      if (hay[i + j] !== need[j]) {
        matched = false;
        break;
      }
    }
    if (matched) return true;
  }
  return false;
}

/** True when any of the candidate's flat `skills` matches `needle` by
 * token boundary. */
function hasSkill(skills: readonly string[], needle: string): boolean {
  return skills.some((skill) => skillMatches(skill, needle));
}

/** Resolve which of a Stelle's Must-/Should-Haves a candidate actually has.
 *
 * Auto-match: whole-token match against `candidate.skills` via
 * `skillMatches` (so "Java" ≠ "JavaScript", "React" = "React Native").
 * Overrides from `candidate.skillMatch` win when present
 * (MVP_requirements.md §4.2). */
export function computeSkillMatch(
  candidate: Pick<Candidate, "skills" | "skillMatch">,
  jobMustHaves: readonly string[],
  jobShouldHaves: readonly string[],
): SkillMatchSummary {
  const mustHit: string[] = [];
  const mustMiss: string[] = [];
  for (const s of jobMustHaves) {
    const override = candidate.skillMatch.mustHaves[s];
    const auto = hasSkill(candidate.skills, s);
    const met = override ?? auto;
    (met ? mustHit : mustMiss).push(s);
  }
  const shouldHit: string[] = [];
  const shouldMiss: string[] = [];
  for (const s of jobShouldHaves) {
    const override = candidate.skillMatch.shouldHaves[s];
    const auto = hasSkill(candidate.skills, s);
    const met = override ?? auto;
    (met ? shouldHit : shouldMiss).push(s);
  }
  return { mustHit, mustMiss, shouldHit, shouldMiss };
}
